import { useCallback, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import css from './views.module.css'
import { Button, Card, Disclosure, MicroLabel, Mono, Notice, QueryError, StatCard } from '../ui'
import {
  FINDINGS_CLEARED_DAYS, useCheckPillars, useDataDesk, useDataFindings, useDataProjectHealth, useProjectChecks,
} from '../data/queries'
import {
  findingsBoard, projectBoards, severityLine, severityTone, toFindings, toProjectStanding, REPORT_STALE_HOURS,
  type DeskFinding, type ProjectFindings,
} from '../data/map/findings'
import {
  checkPillarIndex, inPillar, pillarsByProject, pillarTone,
  type CheckPillarIndex, type PillarCell, type ProjectPillars,
} from '../data/map/pillars'
import type { ReconMap } from '../data/map/recon'
import { count, projectContentsLine } from '../data/map/summaries'
import { age } from '../lib/time'

/**
 * The half of a desk that says what the department *found*: every problem its
 * projects' nightly checks are finding, the ones a person judged expected, and
 * the ones that went away.
 *
 * It reads `v_findings` for the desk's slug and renders nothing when there is
 * nothing, so it sits on every desk without naming a department (product rule 2).
 * Today only the data department reports findings.
 *
 * Nothing on it is a control. A finding clears when the check that found it
 * passes -- that is the whole design, and the reason a desk with nobody assigned
 * to it stays true. The one thing a person decides, "this is expected", is made
 * where every decision is made: dismissing the finding's inbox item, which the
 * row links to.
 *
 * Each project heads its card with one standing per pillar, read from
 * `v_data_desk` and laid out by `map/pillars.ts`. The ratings are the view's;
 * nothing here derives one. A cell is also the drill-down: picking it narrows
 * that project's open list to the pillar, which is the difference between
 * "something is wrong with this database" and "its grants are".
 */
export function DeskFindings({ slug }: { slug: string }) {
  const findings = useDataFindings(slug)
  const health = useDataProjectHealth(slug)
  const desk = useDataDesk()
  const pillars = useCheckPillars()
  const checks = useProjectChecks(slug)

  // The selection lives in the URL, with the department filter and the run
  // trace: it changes what the page shows, so it should survive a reload and be
  // sendable to whoever is being asked about it. Both halves together -- a
  // pillar is only a selection of some project's.
  const [params, setParams] = useSearchParams()
  const pickedProject = params.get('project')
  const pickedPillar = params.get('pillar')

  const pick = useCallback(
    (project: string, pillar: string | null) => {
      const next = new URLSearchParams(params)
      if (pillar === null) {
        next.delete('project')
        next.delete('pillar')
      } else {
        next.set('project', project)
        next.set('pillar', pillar)
      }
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const all = useMemo(() => toFindings(findings.data ?? []), [findings.data])
  const board = useMemo(() => findingsBoard(all), [all])
  const projects = useMemo(
    () => projectBoards(all, toProjectStanding(health.data ?? [])),
    [all, health.data],
  )
  // Scoped by this department's own health rows rather than by its slug: the
  // desk view has no department column, and which projects are a department's
  // is already settled by what reported to it (product rule 2).
  const pillarsByRef = useMemo(
    () => pillarsByProject(desk.data ?? [], pillars.data ?? [], health.data ?? []),
    [desk.data, pillars.data, health.data],
  )
  const checkIndex = useMemo(() => checkPillarIndex(checks.data ?? []), [checks.data])

  if (findings.error) return <QueryError error={findings.error} />
  if (health.error) return <QueryError error={health.error} />
  // Rendered only once both are known, so a desk with no findings does not flash a
  // section on its way to having none.
  if (!findings.data || !health.data) return null
  if (findings.data.length === 0 && health.data.length === 0) return null

  return (
    <section className={css.findings}>
      {/* Totals across every project; each project's own list follows. */}
      <div className={css.deskStats}>
        <StatCard label="Open findings" value={String(board.open.length)} note={severityLine(board.openBySeverity)} />
        <StatCard
          label="With a Linear ticket"
          value={String(board.ticketed)}
          note="critical and high are ticketed; the rest wait here"
        />
        <StatCard
          label="Accepted"
          value={String(board.accepted.length)}
          note="judged expected; the check still finds them"
        />
        <StatCard
          label="Closed"
          value={String(board.closed.length)}
          note={`in the last ${FINDINGS_CLEARED_DAYS} days`}
        />
      </div>

      {projects.map((p) => (
        <ProjectCard
          key={p.ref}
          project={p}
          pillars={pillarsByRef.get(p.ref) ?? null}
          checkIndex={checkIndex}
          checksReady={checks.isSuccess}
          picked={pickedProject === p.ref ? pickedPillar : null}
          onPick={pick}
          defaultOpen={projects.length === 1}
        />
      ))}

      <Notice>
        <span style={{ padding: '0 18px', display: 'block' }}>
          A finding opens the first night a check fails and clears the first night it passes; nobody
          marks one fixed, because the check that found it is what confirms the fix. Critical and high
          findings get a ticket in Linear&rsquo;s Data project, which closes itself when the check clears.
          Fixes to these projects are made by hand &mdash; no agent credential reaches them &mdash; so do
          not move those tickets to Ready to Build. If a finding is expected, dismiss its inbox item
          with a note saying why. Objects are names, never rows: the rows stay in the project that
          found them, and the query shown is where to look.
        </span>
      </Notice>
    </section>
  )
}

/**
 * One database: a standing per pillar from its last report, then what its checks
 * found. Open, accepted and closed stay in the same order and wording they have
 * always had; they are sub-lists of the project now instead of lists of their own.
 *
 * The four cells replace the single overall standing that used to head the card.
 * One word for a whole database said that something was wrong and nothing about
 * what, which is the long list a person opened to find out.
 */
function ProjectCard({ project: p, pillars, checkIndex, checksReady, picked, onPick, defaultOpen }: {
  project: ProjectFindings
  /** Null where the desk view has no row for this project, or its read is still in flight. */
  pillars: ProjectPillars | null
  checkIndex: CheckPillarIndex
  /** Whether that index is the whole one. False while the check list is in flight or failed. */
  checksReady: boolean
  /** The pillar selected on *this* project, if any. */
  picked: string | null
  onPick: (project: string, pillar: string | null) => void
  /** Open on arrival only when it is the desk's one database. */
  defaultOpen: boolean
}) {
  // Closed, a database is its head: the name, when it last reported, what is
  // open by severity and the pillar standings -- which stay clickable, and a
  // pick opens the card to the narrowed list, since that is what it asked for.
  // Closing it again lets go of the pick, so a closed card never hides a filter.
  const [expanded, setExpanded] = useState(defaultOpen)
  const isOpen = expanded || picked !== null
  const s = p.standing
  const { open, accepted, closed } = p.board
  // A selection is a pillar the registry has, on this project. A slug it does
  // not have is not a narrowing anyone can be shown the inside of, so the list
  // stays whole rather than emptying behind a heading nothing can name.
  const pickedCell = pillars?.cells.find((c) => c.slug === picked) ?? null
  // The narrowed list is built by the join, never by the cell's count: the count
  // is the view's arithmetic and the list is this page's, and showing the second
  // under the first is how they are checked against each other in the open.
  const shown = pickedCell ? open.filter((f) => inPillar(checkIndex, f, pickedCell.slug)) : open
  return (
    <Card style={{ marginBottom: 14 }}>
      <Disclosure
        title={p.project}
        open={isOpen}
        onOpenChange={(next) => {
          setExpanded(next)
          if (!next && picked !== null) onPick(p.ref, null)
        }}
        summary={
          <span className={`${css.ticketLanes} mono`} style={{ marginTop: 0 }}>
            <span>{p.ref}</span>
            {s ? <span>reported {age(s.lastReportAt)}</span> : <span>no report on record</span>}
            <span>{projectContentsLine(p)}</span>
            {/* Absent rather than failing when either half is null: 98h-infra
                expects nothing of some projects, and "no contract" is not
                "does not match its contract". */}
            {pillars?.contractVersion ? <span>contract {pillars.contractVersion}</span> : null}
            {pillars?.contractOk === true ? <span>matches its contract</span> : null}
            {pillars?.contractOk === false ? (
              <span style={{ color: 'var(--s-fail)' }}>does not match its contract</span>
            ) : null}
          </span>
        }
        aside={
          <div className={css.findingStanding}>
            {pillars && pillars.cells.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {pillars.cells.map((c) => (
                  <PillarButton
                    key={c.slug}
                    cell={c}
                    picked={picked === c.slug}
                    onPick={() => onPick(p.ref, picked === c.slug ? null : c.slug)}
                  />
                ))}
              </div>
            ) : (
              // No row in the desk view for this project, no pillar registry to
              // lay one out with, or neither read has landed. Its last report's own
              // word, which is what headed this card before the pillars did.
              <span>{s ? s.standing : 'no summary on record'}</span>
            )}
            {s?.silent ? (
              <div className={css.ticketFail}>
                No report in {REPORT_STALE_HOURS} hours, so everything below is older than it looks.
              </div>
            ) : null}
            {s?.checksOverdue ? (
              <div className={css.ticketFail}>
                Its own checks have stopped running; the report is of old results.
              </div>
            ) : null}
          </div>
        }
      >
        {s?.recon ? <Reconciliation recon={s.recon} /> : null}

        <div className={css.findingSubHead}>
          <MicroLabel>Open</MicroLabel>
          {pickedCell ? (
            <span className={css.ticketHeadNote}>
              {pickedCell.label} only &mdash; {pickedCell.question}
              <Button variant="quiet" onClick={() => onPick(p.ref, null)} style={{ marginLeft: 10 }}>
                Show every pillar
              </Button>
            </span>
          ) : (
            <span className={css.ticketHeadNote}>worst first, then the longest standing</span>
          )}
        </div>
        {shown.length === 0 ? (
          <p className={css.findingNone}>
            {!pickedCell
              ? 'Nothing open. Every check this project runs is passing.'
              : checksReady
              ? `Nothing open in ${pickedCell.label} for this project. Its other pillars are unchanged; `
                + 'this list is narrowed, not empty.'
              // Which pillar each finding is in comes from the check list, and that
              // read has not landed. Saying the pillar is clear would be this page
              // reporting a missing join as a clean bill of health.
              : `Which of this project's findings are in ${pickedCell.label} is not known right now — `
                + 'the check list could not be read. Show every pillar to see them all.'}
          </p>
        ) : (
          shown.map((f) => <OpenFinding key={f.id} finding={f} />)
        )}

        {/* Accepted and closed are the record, not the news: closed to a count
            under the open list, which is what a person opened the card to read. */}
        {accepted.length > 0 ? (
          <div className={css.findingSubGroup}>
            <Disclosure
              label
              title="Accepted"
              summary={`${count(accepted.length, 'finding')} · a person dismissed the inbox item; the check still finds them`}
            >
              {accepted.map((f) => (
                <div key={f.id} className={css.findingRow}>
                  <Severity finding={f} />
                  <div>
                    <div className={css.ticketTitle}>{f.title}</div>
                    <div className={`${css.ticketLanes} mono`}>
                      <span>{f.check}</span>
                      {f.countLabel ? <span>{f.countLabel}</span> : null}
                    </div>
                    <div className={css.findingNote}>
                      {f.decisionNote ? `“${f.decisionNote}”` : 'No note given.'}
                      {f.decidedBy ? ` — ${f.decidedBy}` : ''}
                    </div>
                    <Links finding={f} />
                  </div>
                </div>
              ))}
            </Disclosure>
          </div>
        ) : null}

        {closed.length > 0 ? (
          <div className={css.findingSubGroup}>
            <Disclosure label title="Closed" summary={`${count(closed.length, 'finding')} · most recent first`}>
              {closed.map((f) => (
                <div key={f.id} className={css.findingRow}>
                  <Severity finding={f} />
                  <div>
                    <div className={css.ticketTitle}>{f.title}</div>
                    <div className={`${css.ticketLanes} mono`}>
                      <span>{f.check}</span>
                      <span>
                        {f.state === 'retired' ? 'no longer reported' : 'passed'} {age(f.clearedAt)}
                      </span>
                      <span>open since {age(f.firstSeenAt)}</span>
                    </div>
                    {f.state === 'retired' ? (
                      <div className={css.findingNote}>
                        The check stopped being reported. That is not evidence it was fixed, so any ticket
                        is left open for a person to decide.
                      </div>
                    ) : null}
                    <Links finding={f} />
                  </div>
                </div>
              ))}
            </Disclosure>
          </div>
        ) : null}
      </Disclosure>
    </Card>
  )
}

/**
 * One pillar's standing, and the way into its findings.
 *
 * A filter pill, the same mechanism the lanes and the feed use for the
 * department filter, squared off and given two lines so the pillar's name and
 * its standing both fit. 44px tall, because a desk is cleared from a phone
 * between meetings as much as from a desk (product rule 5).
 *
 * The status is a word in a muted token and nothing else. No badge, no fill, no
 * count in a colour: a database with seven open access findings must read as a
 * fact on a page, not as an alarm (product rule 1). `unknown` takes plain ink --
 * never green, which would say a pillar nobody checks is passing.
 */
function PillarButton({ cell, picked, onPick }: {
  cell: PillarCell
  picked: boolean
  onPick: () => void
}) {
  return (
    <button
      className={picked ? `${css.filterPill} ${css.filterActive}` : css.filterPill}
      style={{ borderRadius: 4, padding: '6px 10px', minHeight: 44, minWidth: 96, textAlign: 'left', flex: '1 1 96px' }}
      aria-pressed={picked}
      title={cell.question}
      onClick={onPick}
    >
      <MicroLabel>{cell.label}</MicroLabel>
      <span style={{ color: pillarTone(cell.status) }}>{cell.standing}</span>
      {cell.open > 0 ? <span className={css.ticketAge} style={{ marginTop: 0 }}> &middot; {cell.open} open</span> : null}
    </button>
  )
}

function Severity({ finding }: { finding: DeskFinding }) {
  return (
    <div className="mono" style={{ fontSize: 12 }}>
      <span style={{ color: severityTone(finding.severity) }}>{finding.severity ?? 'unrated'}</span>
      <div className={css.ticketAge}>{finding.category}</div>
    </div>
  )
}

function Links({ finding }: { finding: DeskFinding }) {
  const t = finding.ticket
  if (!t && !finding.approvalId) return null
  return (
    <div className={css.ticketLinks}>
      {t ? (
        t.url ? (
          <a href={t.url} target="_blank" rel="noreferrer">{t.ref}{t.state ? ` · ${t.state}` : ''}</a>
        ) : (
          <Mono>{t.ref}{t.state ? ` · ${t.state}` : ''}</Mono>
        )
      ) : null}
      {finding.approvalId ? <Link to={`/inbox/${finding.approvalId}`}>Inbox item</Link> : null}
    </div>
  )
}

function OpenFinding({ finding: f }: { finding: DeskFinding }) {
  return (
    <div className={css.findingRow}>
      <Severity finding={f} />
      <div>
        <div className={css.ticketTitle}>{f.title}</div>
        <div className={`${css.ticketLanes} mono`}>
          <span>{f.check}</span>
          {f.countLabel ? <span>{f.countLabel}</span> : null}
          <span>first seen {age(f.firstSeenAt)}</span>
          <span>{f.reportsSeen} {f.reportsSeen === 1 ? 'report' : 'reports'}</span>
          <span>last seen {age(f.lastSeenAt)}</span>
        </div>
        {f.error ? <div className={css.ticketFail}>The check could not run: {f.error}</div> : null}
        <Links finding={f} />
        {f.objects.length > 0 || f.sourceQuery ? (
          <details className={css.findingDetails}>
            <summary>
              {f.objects.length > 0
                ? `What it is about (${f.objects.length} ${f.objects.length === 1 ? 'object' : 'objects'})`
                : 'Where to look'}
            </summary>
            {f.sourceQuery ? (
              <div className={css.findingQuery}>
                In the project: <Mono>{f.sourceQuery}</Mono>
              </div>
            ) : null}
            {f.objects.map((o, i) => (
              <div key={`${o.name}-${o.grantee ?? ''}-${i}`} className={css.findingObject}>
                <Mono>{o.name}</Mono>
                <span className={css.ticketAge}>
                  {[o.grantee, o.severity].filter(Boolean).join(' · ')}
                </span>
                {o.hint ? <div className={`${css.findingHint} mono`}>{o.hint}</div> : null}
              </div>
            ))}
            {f.objects.length > 0 ? (
              <div className={css.findingNote}>
                The hints are the SQL that would close each one, for a person to judge. They are not
                instructions.
              </div>
            ) : null}
          </details>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The project's reconciliation map, as its last report stated it (contract v10): which
 * subsidiaries make up each entity, how close figures must agree, and the rest of the
 * decision -- who owns a variance, the calendar, the lock rule.
 *
 * Closed by default to one line of entities, because it is reference, not news: it
 * changes when a person decides something, not night to night. It sits in the project's
 * card rather than above the findings so the findings it explains stay the first thing
 * read. Nothing here is a control, and none of it is styled as a state.
 */
function Reconciliation({ recon }: { recon: ReconMap }) {
  return (
    <>
      <div className={css.findingSubHead}>
        <MicroLabel>Reconciliation</MicroLabel>
        <span className={css.ticketHeadNote}>as this project&rsquo;s checks ran it last night</span>
      </div>
      <div className={css.reconBody}>
        <details className={css.findingDetails}>
          <summary className="mono">{recon.line || 'the map'}</summary>

          {recon.entities.length > 0 ? (
            <div className={css.reconBlock}>
              {recon.entities.map((e) => (
                <div key={e.entity} className={css.reconRow}>
                  <span className={css.ticketTitle}>{e.entity}</span>
                  <span>
                    NetSuite subsidiar{e.subsidiaries.length === 1 ? 'y' : 'ies'}{' '}
                    <Mono>{e.subsidiaries.join(', ')}</Mono>
                    {e.notes ? <span className={css.findingNote}> &mdash; {e.notes}</span> : null}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {recon.tolerances.length > 0 ? (
            <div className={css.reconBlock}>
              {recon.tolerances.map((t) => (
                <div key={t.comparison} className={css.reconRow}>
                  <span className={css.ticketTitle}>{t.comparison}</span>
                  <span>
                    {t.rule}
                    {t.notes ? <div className={css.findingNote}>{t.notes}</div> : null}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {recon.notes.length > 0 ? (
            <div className={css.reconBlock}>
              {recon.notes.map((n) => (
                <div key={n.topic} className={css.reconRow}>
                  <span className={css.ticketTitle}>{n.label}</span>
                  <span className={css.reconNote}>{n.note}</span>
                </div>
              ))}
            </div>
          ) : null}
        </details>
      </div>
    </>
  )
}
